import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

interface FullscreenEnforcerOptions {
  enabled: boolean;
  warningText: string;
  reenterDelayMs?: number;
}

export const useFullscreenEnforcer = ({
  enabled,
  warningText,
  reenterDelayMs = 2000,
}: FullscreenEnforcerOptions) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const followUpTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastIdRef = useRef<string | number | undefined>(undefined);
  const pendingRestoreRef = useRef<boolean>(false);
  const hasEverBeenFullscreenRef = useRef<boolean>(false);
  const lastAttemptTsRef = useRef<number>(0);

  const clearReentryTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (followUpTimeoutRef.current) {
      clearTimeout(followUpTimeoutRef.current);
      followUpTimeoutRef.current = null;
    }
  }, []);

  const dismissWarning = useCallback(() => {
    if (toastIdRef.current !== undefined) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = undefined;
    }
  }, []);

  const requestFullscreen = useCallback(async () => {
    if (!enabled) return;
    const now = Date.now();
    if (now - lastAttemptTsRef.current < 400) {
      return;
    }
    lastAttemptTsRef.current = now;

    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement === container) {
      pendingRestoreRef.current = false;
      hasEverBeenFullscreenRef.current = true;
      dismissWarning();
      clearReentryTimer();
      return;
    }
    try {
      if (container.requestFullscreen) {
        await container.requestFullscreen();
        pendingRestoreRef.current = false;
        hasEverBeenFullscreenRef.current = true;
        dismissWarning();
        clearReentryTimer();
        // Try to lock keyboard to soften ESC exits (best-effort)
        const keyboard = (navigator as any)?.keyboard as { lock?: (keys?: string[]) => Promise<void>; unlock?: () => void };
        if (keyboard?.lock) {
          void keyboard.lock(['Escape']).catch(() => {});
        }
      }
    } catch {
      // Browser may reject without a direct user gesture; keep pending flag.
      pendingRestoreRef.current = true;
    }
  }, [clearReentryTimer, dismissWarning, enabled]);

  const scheduleReentry = useCallback(() => {
    if (!enabled) return;
    clearReentryTimer();
    timeoutRef.current = setTimeout(() => {
      void requestFullscreen();
    }, reenterDelayMs);
    // A second follow-up attempt after a bit longer to handle stricter debouncing
    followUpTimeoutRef.current = setTimeout(() => {
      void requestFullscreen();
    }, reenterDelayMs + 1200);
  }, [clearReentryTimer, enabled, reenterDelayMs, requestFullscreen]);

  const showWarning = useCallback(() => {
    if (!enabled) return;
    if (!hasEverBeenFullscreenRef.current) return; // Avoid startup noise
    if (toastIdRef.current !== undefined) return;
    toastIdRef.current = toast.warning(warningText, {
      duration: Infinity,
      position: 'bottom-right',
    });
  }, [enabled, warningText]);

  const handleExit = useCallback(() => {
    pendingRestoreRef.current = true;
    showWarning();
    scheduleReentry();
    void requestFullscreen();
  }, [requestFullscreen, scheduleReentry, showWarning]);

  // Watch fullscreen changes
  useEffect(() => {
    if (!enabled) return;
    const onFullscreenChange = () => {
      if (document.fullscreenElement === containerRef.current) {
        pendingRestoreRef.current = false;
        hasEverBeenFullscreenRef.current = true;
        dismissWarning();
        clearReentryTimer();
        const keyboard = (navigator as any)?.keyboard as { lock?: (keys?: string[]) => Promise<void>; unlock?: () => void };
        if (keyboard?.unlock) {
          keyboard.unlock();
        }
        if (keyboard?.lock) {
          void keyboard.lock(['Escape']).catch(() => {});
        }
      } else {
        handleExit();
        const keyboard = (navigator as any)?.keyboard as { lock?: (keys?: string[]) => Promise<void>; unlock?: () => void };
        if (keyboard?.unlock) {
          keyboard.unlock();
        }
      }
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);
    // Initial attempt
    void requestFullscreen();

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      clearReentryTimer();
      dismissWarning();
    };
  }, [clearReentryTimer, dismissWarning, enabled, handleExit, requestFullscreen]);

  // Use user gestures (pointer/keydown) to satisfy fullscreen API requirements
  useEffect(() => {
    if (!enabled) return;
    const handleUserGesture = (event: Event) => {
      if (event instanceof KeyboardEvent && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (typeof (event as any).stopImmediatePropagation === 'function') {
          (event as any).stopImmediatePropagation();
        }
        pendingRestoreRef.current = true;
        void requestFullscreen();
        queueMicrotask(() => {
          if (pendingRestoreRef.current) {
            void requestFullscreen();
          }
        });
        return;
      }

      if (pendingRestoreRef.current) {
        void requestFullscreen();
      }
    };

    document.addEventListener('pointerdown', handleUserGesture, { capture: true });
    document.addEventListener('keydown', handleUserGesture, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', handleUserGesture, { capture: true });
      document.removeEventListener('keydown', handleUserGesture, { capture: true });
    };
  }, [enabled, requestFullscreen]);

  // Cleanup when disabled
  useEffect(() => {
    if (!enabled) {
      clearReentryTimer();
      dismissWarning();
      pendingRestoreRef.current = false;
    }
  }, [clearReentryTimer, dismissWarning, enabled]);

  return { containerRef };
};
